import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, Car, Home as HomeIcon, BadgeEuro, Wallet, Mail, Instagram } from "lucide-react";
import { LOGO_URL, COMPANY, buildWhatsappUrl, openWhatsapp, WA_MESSAGES, phoneHref } from "../lib/contact";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/fahrzeuge", label: "Fahrzeuge" },
  { to: "/fahrzeug-verkaufen", label: "Verkaufen" },
  { to: "/finanzierung", label: "Finanzierung" },
  { to: "/kontakt", label: "Kontakt" },
];

export const Header = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <header
      data-testid="site-header"
      className="glass border-b border-white/10 sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 lg:gap-4 group" aria-label="NA Automobile">
          <img
            src={LOGO_URL}
            alt="NA Automobile"
            className="h-9 sm:h-10 lg:h-12 w-auto select-none transition-transform duration-300 group-hover:scale-[1.04]"
            draggable={false}
          />
          <span className="hidden md:flex flex-col border-l border-white/10 pl-3 lg:pl-4 leading-tight">
            <span className="text-[10px] lg:text-[11px] uppercase tracking-[0.28em] text-zinc-300 font-medium">Wien</span>
            <span className="text-[9px] lg:text-[10px] uppercase tracking-[0.32em] text-zinc-500 mt-0.5">Premium Cars</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-[var(--na-red)]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <a
            href={phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <Phone className="w-4 h-4" strokeWidth={1.6} />
            {COMPANY.phone}
          </a>
          <a
            href={buildWhatsappUrl(WA_MESSAGES.general)}
            onClick={openWhatsapp(WA_MESSAGES.general)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-[#25D366] text-[#1A1A22] hover:bg-[#20bd5a] transition-colors"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
            WhatsApp
          </a>
        </div>

        <button
          aria-label="Menu"
          data-testid="header-menu-toggle"
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#1A1A22]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-xl text-base ${
                    isActive ? "text-white bg-white/5" : "text-zinc-300 hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-3 mt-2 border-t border-white/10">
              <a
                href={phoneHref}
                data-testid="mobile-header-phone"
                className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-white/15 text-white"
              >
                <Phone className="w-4 h-4" strokeWidth={1.8} />
                Anrufen
              </a>
              <a
                href={buildWhatsappUrl(WA_MESSAGES.general)}
                onClick={openWhatsapp(WA_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-header-whatsapp"
                className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-[#25D366] text-[#1A1A22] font-medium"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t border-white/5 bg-[#1A1A22] mt-0 pb-20 lg:pb-0">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12 grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-10">
      <div className="space-y-4">
        <img src={LOGO_URL} alt="NA Automobile" className="h-12 w-auto" />
        <p className="text-sm text-zinc-400 leading-relaxed max-w-xs">
          Premium Fahrzeuge, Ankauf, Verkauf und Finanzierung in Wien. Auch
          beschädigte und nicht fahrbereite Fahrzeuge.
        </p>
      </div>

      <div>
        <h4 className="font-display font-semibold text-white mb-4 text-sm uppercase tracking-widest">
          Navigation
        </h4>
        <ul className="space-y-2.5 text-sm">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="text-zinc-400 hover:text-white transition-colors">
                {l.label}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/impressum" className="text-zinc-400 hover:text-white transition-colors">
              Impressum
            </Link>
          </li>
          <li>
            <Link to="/datenschutz" className="text-zinc-400 hover:text-white transition-colors">
              Datenschutz
            </Link>
          </li>
          <li className="pt-2 mt-2 border-t border-white/5">
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="text-zinc-500 hover:text-[var(--na-red)] transition-colors text-xs uppercase tracking-widest"
            >
              Admin Login
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-display font-semibold text-white mb-4 text-sm uppercase tracking-widest">
          Kontakt
        </h4>
        <ul className="space-y-2.5 text-sm text-zinc-400">
          <li>{COMPANY.name}</li>
          <li>{COMPANY.address}</li>
          <li>{COMPANY.zipCity}</li>
          <li className="pt-2">
            <a href={phoneHref} className="hover:text-white">{COMPANY.phone}</a>
          </li>
          <li>
            <a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-display font-semibold text-white mb-4 text-sm uppercase tracking-widest">
          Öffnungszeiten
        </h4>
        <ul className="space-y-2.5 text-sm text-zinc-400">
          <li>{COMPANY.hours.weekday}</li>
          <li>{COMPANY.hours.sat}</li>
          <li>{COMPANY.hours.sun}</li>
        </ul>
        <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
          UID: {COMPANY.vat}
          <br />
          {COMPANY.legal}
        </div>
      </div>
    </div>
    <div className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
        <p className="order-2 sm:order-1">© {new Date().getFullYear()} {COMPANY.name} · {COMPANY.legal}</p>
        <a
          href="https://instagram.com/cloudservice.hr"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-credit-link"
          className="order-1 sm:order-2 inline-flex items-center gap-1.5 text-zinc-500 hover:text-white transition-colors"
        >
          <span>Made by</span>
          <Instagram className="w-3.5 h-3.5" />
          <span className="font-medium tracking-wide">cloudservice.hr</span>
        </a>
        <p className="order-3">Made in Wien · Drive your dream</p>
      </div>
    </div>
  </footer>
);

export const WhatsAppFAB = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return (
    <a
      href={buildWhatsappUrl(WA_MESSAGES.general)}
      onClick={openWhatsapp(WA_MESSAGES.general)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp-fab"
      aria-label="WhatsApp Chat"
      className="fixed bottom-24 lg:bottom-8 right-4 lg:right-8 w-14 h-14 lg:w-16 lg:h-16 bg-[#25D366] rounded-full flex items-center justify-center text-white whatsapp-glow hover:scale-110 transition-transform z-40 pulse-ring"
    >
      <MessageCircle className="w-6 h-6 lg:w-7 lg:h-7" strokeWidth={2} />
    </a>
  );
};

export const StickyMobileNav = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  const items = [
    { to: "/", label: "Home", icon: HomeIcon },
    { to: "/fahrzeuge", label: "Fahrzeuge", icon: Car },
    { to: "/fahrzeug-verkaufen", label: "Verkauf", icon: BadgeEuro },
    { to: "/finanzierung", label: "Finanz", icon: Wallet },
  ];
  return (
    <nav
      data-testid="sticky-mobile-nav"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 glass border-t border-white/10 h-16 px-2 flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]"
    >
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === "/"}
          data-testid={`mobile-tab-${it.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex flex-col items-center justify-center gap-0.5 flex-1 text-[10px] tracking-widest uppercase ${
              isActive ? "text-white" : "text-zinc-400"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <it.icon className="w-5 h-5" strokeWidth={1.7} />
              <span>{it.label}</span>
              {isActive && <span className="block w-6 h-0.5 bg-[var(--na-red)] mt-0.5" />}
            </>
          )}
        </NavLink>
      ))}
      <a
        href={buildWhatsappUrl(WA_MESSAGES.general)}
        onClick={openWhatsapp(WA_MESSAGES.general)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-tab-whatsapp"
        className="flex flex-col items-center justify-center gap-0.5 flex-1 text-[10px] tracking-widest uppercase text-[#25D366]"
      >
        <MessageCircle className="w-5 h-5" strokeWidth={1.9} />
        <span>WhatsApp</span>
      </a>
    </nav>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <div className="min-h-screen flex flex-col bg-[var(--na-bg)] text-white">
      {!isAdmin && <Header />}
      <main className="flex-1">{children}</main>
      {!isAdmin && <Footer />}
      <WhatsAppFAB />
      <StickyMobileNav />
    </div>
  );
};

export default Layout;
